import React, { useMemo, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { Bar, Doughnut } from "react-chartjs-2";

const getSafeNum = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const getDiffClass = (value) => (getSafeNum(value) >= 0 ? "up" : "down");
const CPT_CODE_COLORS = {
  99439: "#8b5cf6",
  99445: "#10b981",
  99453: "#f97316",
  99454: "#ef4444",
  99457: "#7c3aed",
  99458: "#ec4899",
  99470: "#14b8a6",
  99490: "#84cc17"
};

const getCptCodePillStyle = (code) => ({
  background: CPT_CODE_COLORS[code] || "#64748b",
  color: "#ffffff"
});

const SummaryCards = ({ summary, comparison, dashboard }) => {
  const baseMonth = dashboard?.baseMonth || {};
  const comparedMonth = dashboard?.comparedMonth || {};
  const baseMonthLabel = baseMonth?.label || "Base";
  const comparedMonthLabel = comparedMonth?.label || "Compared";
  const deriveBaseValue = (comparedValue, diffValue) => getSafeNum(comparedValue) - getSafeNum(diffValue);
  const pickComparedValue = (preferred, fallback) =>
    preferred !== undefined && preferred !== null ? getSafeNum(preferred) : getSafeNum(fallback);
  const formatMetric = (value, isPercent) => `${getSafeNum(value)}${isPercent ? "%" : ""}`;

  const cards = [
    {
      label: "Total Patients",
      comparedValue: pickComparedValue(comparedMonth?.totalPatients, summary?.totalPatients),
      baseValue: getSafeNum(
        baseMonth?.totalPatients ??
          deriveBaseValue(
            pickComparedValue(comparedMonth?.totalPatients, summary?.totalPatients),
            comparison?.totalPatientsDiff
          )
      ),
      diff: getSafeNum(comparison?.totalPatientsDiff),
      icon: "mdi-account-group",
      tone: "blue",
      isPercent: false
    },
    {
      label: "Fully Billable",
      comparedValue: pickComparedValue(comparedMonth?.fullyBillable, summary?.fullyBillable),
      baseValue: getSafeNum(
        baseMonth?.fullyBillable ??
          deriveBaseValue(
            pickComparedValue(comparedMonth?.fullyBillable, summary?.fullyBillable),
            comparison?.fullyBillableDiff
          )
      ),
      diff: getSafeNum(comparison?.fullyBillableDiff),
      icon: "mdi-check-circle",
      tone: "green",
      isPercent: false
    },
    {
      label: "Average Achievement",
      comparedValue: pickComparedValue(comparedMonth?.averageAchievement, summary?.averageAchievement),
      baseValue: getSafeNum(
        baseMonth?.averageAchievement ??
          deriveBaseValue(
            pickComparedValue(comparedMonth?.averageAchievement, summary?.averageAchievement),
            comparison?.averageAchievementDiff
          )
      ),
      diff: getSafeNum(comparison?.averageAchievementDiff),
      icon: "mdi-trending-up",
      tone: "yellow",
      isPercent: true
    },
    {
      label: "Compliance Rate",
      comparedValue: pickComparedValue(comparedMonth?.complianceRate, summary?.complianceRate),
      baseValue: getSafeNum(
        baseMonth?.complianceRate ??
          deriveBaseValue(
            pickComparedValue(comparedMonth?.complianceRate, summary?.complianceRate),
            comparison?.complianceRateDiff
          )
      ),
      diff: getSafeNum(comparison?.complianceRateDiff),
      icon: "mdi-shield-check",
      tone: "red",
      isPercent: true
    }
  ];

  return (
    <Row className="mpr-stat-row">
      {cards.map((card) => (
        <Col md="3" key={card.label}>
          <Card className={`mpr-stat-card mpr-tone-${card.tone}`}>
            <CardBody>
              <div className="mpr-stat-meta">
                <span className="mpr-stat-title">{card.label}</span>
                {/* <h3>{formatMetric(card.comparedValue, card.isPercent)}</h3> */}
                <div className="mpr-stat-compare">
                  <span className="mpr-stat-compare-item">
                    <strong>{baseMonthLabel}:</strong> {formatMetric(card.baseValue, card.isPercent)}
                  </span>
                  <span className="mpr-stat-compare-item">
                    <strong>{comparedMonthLabel}:</strong> {formatMetric(card.comparedValue, card.isPercent)}
                  </span>
                </div>
                <div className={`mpr-diff ${getDiffClass(card.diff)}`}>
                  <i className={`mdi ${card.diff >= 0 ? "mdi-arrow-up-bold" : "mdi-arrow-down-bold"}`}></i>
                  {card.diff >= 0 ? "+" : ""}
                  {formatMetric(card.diff, card.isPercent)}
                </div>
              </div>
              <div className={`mpr-stat-icon ${card.tone}`}>
                <i className={`mdi ${card.icon}`}></i>
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

const MonthlyPerformanceCharts = ({ dashboard }) => {
  const [sortConfig, setSortConfig] = useState({ key: "code", direction: "asc" });
  const baseMonth = dashboard?.baseMonth || {};
  const comparedMonth = dashboard?.comparedMonth || {};
  const baseMonthLabel = baseMonth?.label || "Base Month";
  const comparedMonthLabel = comparedMonth?.label || "Compared Month";
  const comparison = dashboard?.comparison || {};
  const cptCodeComparison = Array.isArray(comparison?.cptCodeComparison) ? comparison.cptCodeComparison : [];
  const partialCount =
    getSafeNum(comparedMonth?.partial) ||
    (getSafeNum(comparedMonth?.componentSummary?.onlyTechnical) +
      getSafeNum(comparedMonth?.componentSummary?.onlyProfessional));

  const donutData = {
    labels: ["Met Both", "Met Only One", "Met None"],
    datasets: [
      {
        data: [
          getSafeNum(comparedMonth?.componentSummary?.bothMet),
          partialCount,
          getSafeNum(comparedMonth?.componentSummary?.neither)
        ],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
        borderWidth: 1
      }
    ]
  };

  const compareData = {
    labels: ["Fully Billable", "Partial", "Not Billable"],
    datasets: [
      {
        label: baseMonthLabel,
        data: [getSafeNum(baseMonth?.fullyBillable), getSafeNum(baseMonth?.partial), getSafeNum(baseMonth?.notBillable)],
        backgroundColor: "#0ea5e9"
      },
      {
        label: comparedMonthLabel,
        data: [
          getSafeNum(comparedMonth?.fullyBillable),
          getSafeNum(comparedMonth?.partial),
          getSafeNum(comparedMonth?.notBillable)
        ],
        backgroundColor: "#8b5cf6"
      }
    ]
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "mdi mdi-sort";
    return sortConfig.direction === "asc" ? "mdi mdi-sort-ascending" : "mdi mdi-sort-descending";
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const sortedCptCodeComparison = useMemo(() => {
    const getValue = (row, key) => {
      switch (key) {
        case "code":
          return row.code || "";
        case "name":
          return row.name || "";
        case "baseMonthPercentage":
          return getSafeNum(row.baseMonthPercentage);
        case "comparedMonthPercentage":
          return getSafeNum(row.comparedMonthPercentage);
        case "percentageDiff":
          return getSafeNum(row.percentageDiff);
        case "trend":
          return row.trend || "";
        default:
          return "";
      }
    };

    return [...cptCodeComparison].sort((a, b) => {
      const aVal = getValue(a, sortConfig.key);
      const bVal = getValue(b, sortConfig.key);
      const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * directionMultiplier;
      }
      return String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" }) * directionMultiplier;
    });
  }, [cptCodeComparison, sortConfig]);

  return (
    <Row className="mpr-chart-row">
      <Col md="5">
        <Card className="mpr-chart-card">
          <CardBody>
            <div className="chart-header-modern">
              <div className="chart-icon-modern">
                <i className="mdi mdi-chart-donut"></i>
              </div>
              <div className="chart-header-copy-modern">
                <h4>{`${comparedMonthLabel} Performance Split`}</h4>
                <p>Shows patients who met both, one, or none of technical and professional CPT components.</p>
              </div>
            </div>
            <div className="mpr-donut-wrap">
              <div className="mpr-donut-chart-shell">
                <Doughnut
                  data={donutData}
                  options={{
                    maintainAspectRatio: false,
                    cutout: "65%",
                    plugins: { legend: { position: "bottom" } }
                  }}
                />
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col md="7">
        <Card className="mpr-chart-card">
          <CardBody>
            <div className="chart-header-modern">
              <div className="chart-icon-modern">
                <i className="mdi mdi-chart-bar"></i>
              </div>
              <div className="chart-header-copy-modern">
                <h4>{`${baseMonthLabel} vs ${comparedMonthLabel}`}</h4>
                <p>{`Monthly comparison of billable outcomes between ${baseMonthLabel} and ${comparedMonthLabel}.`}</p>
              </div>
            </div>
            <div className="mpr-bar-wrap">
              <Bar
                data={compareData}
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "bottom" } },
                  scales: { y: { beginAtZero: true } }
                }}
              />
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col md="12" className="mt-3">
        <Card className="mpr-chart-card">
          <CardBody>
            <div className="chart-header-modern">
              <div className="chart-icon-modern trend">
                <i className="mdi mdi-trending-up"></i>
              </div>
              <div className="chart-header-copy-modern">
                <h4>CPT Code Trend Analytics</h4>
                <p>{`Performance trend by CPT code from ${baseMonthLabel} to ${comparedMonthLabel}.`}</p>
              </div>
            </div>
            <div className="table-responsive">
              <table className="mpr-trend-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("code")} className="mpr-sortable-header">CPT Code <i className={getSortIcon("code")}></i></th>
                    <th onClick={() => handleSort("name")} className="mpr-sortable-header">Name <i className={getSortIcon("name")}></i></th>
                    <th onClick={() => handleSort("baseMonthPercentage")} className="mpr-sortable-header">{`${baseMonthLabel} %`} <i className={getSortIcon("baseMonthPercentage")}></i></th>
                    <th onClick={() => handleSort("comparedMonthPercentage")} className="mpr-sortable-header">{`${comparedMonthLabel} %`} <i className={getSortIcon("comparedMonthPercentage")}></i></th>
                    <th onClick={() => handleSort("percentageDiff")} className="mpr-sortable-header">Difference <i className={getSortIcon("percentageDiff")}></i></th>
                    <th onClick={() => handleSort("trend")} className="mpr-sortable-header">Trend <i className={getSortIcon("trend")}></i></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCptCodeComparison.map((codeRow) => (
                    <tr key={codeRow.code}>
                      <td>
                        <span className="mpr-cpt-code-pill" style={getCptCodePillStyle(codeRow.code)}>
                          {codeRow.code}
                        </span>
                      </td>
                      <td>{codeRow.name || "-"}</td>
                      <td>{getSafeNum(codeRow.baseMonthPercentage)}%</td>
                      <td>{getSafeNum(codeRow.comparedMonthPercentage)}%</td>
                      <td className={getSafeNum(codeRow.percentageDiff) >= 0 ? "mpr-text-up" : "mpr-text-down"}>
                        {getSafeNum(codeRow.percentageDiff) >= 0 ? "+" : ""}
                        {getSafeNum(codeRow.percentageDiff)}%
                      </td>
                      <td>
                        <span className={`mpr-trend-badge ${codeRow.trend === "up" ? "up" : "down"}`}>
                          <i className={`mdi ${codeRow.trend === "up" ? "mdi-trending-up" : "mdi-trending-down"}`}></i>
                          {codeRow.trend === "up" ? "Improved" : "Declined"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {sortedCptCodeComparison.length === 0 && (
                    <tr>
                      <td colSpan="6" className="mpr-empty-row">
                        No CPT trend analytics available for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export { SummaryCards };
export default MonthlyPerformanceCharts;
